import { MAX_SUBJECT_PHOTO_BYTES, classifyUpload, photoProblemHelp } from "./constants";

/**
 * Get a dropped image into something the server will accept, instead of telling the agent no.
 *
 * WHY THIS EXISTS. The server takes PNG, JPEG, GIF or WEBP at 6 MB or less. An iPhone shoots HEIC by
 * default and a modern phone camera produces files well over 6 MB — so the two most likely photos a
 * real agent hands us were both rejections. "Ask the user to go and convert it" is a worse product
 * than doing the conversion.
 *
 * TWO TRANSFORMS, both in the browser:
 *   1. HEIC/HEIF -> JPEG, via libheif compiled to WASM (`heic2any`). It is ~2.7 MB, so it is loaded
 *      with a DYNAMIC import and only when a HEIC is actually dropped. It must never reach the main
 *      bundle: almost every session will not need it.
 *   2. Too large -> downscaled. A report hero image is printed a few inches wide; a 12-megapixel
 *      original is nothing but upload time. Longest edge capped, re-encoded, and if the result is
 *      still over the ceiling the quality steps down until it fits.
 *
 * Everything already small and already supported passes through UNTOUCHED — no needless re-encode of
 * a photo that was fine, and the server normalizes it anyway.
 */

const MAX_EDGE_PX = 1800;
const QUALITY_LADDER = [0.85, 0.7, 0.55, 0.4];

export interface PreparedPhoto {
  file: File | null;
  /** Said to the agent when the file was changed on the way through, so a silent transform never
   * happens to their evidence. Null when the file passed through untouched. */
  note: string | null;
  /** Set when nothing could be done; already phrased as an instruction, not a rejection. */
  error: string | null;
}

function renameTo(name: string, ext: string): string {
  return name.replace(/\.[^.]+$/, "") + "." + ext;
}

async function loadBitmap(blob: Blob): Promise<{ width: number; height: number; draw: (c: CanvasRenderingContext2D, w: number, h: number) => void } | null> {
  // `createImageBitmap` is the fast path and avoids an <img> round trip; the object-URL fallback
  // exists because jsdom and older Safari lack it.
  try {
    if (typeof createImageBitmap === "function") {
      const bmp = await createImageBitmap(blob);
      return { width: bmp.width, height: bmp.height, draw: (c, w, h) => c.drawImage(bmp, 0, 0, w, h) };
    }
  } catch {
    /* fall through */
  }
  return await new Promise((resolve) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight,
                draw: (c, w, h) => c.drawImage(img, 0, 0, w, h) });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });
}

async function shrink(file: File): Promise<File | null> {
  const bmp = await loadBitmap(file);
  if (!bmp || !bmp.width || !bmp.height) return null;
  const scale = Math.min(1, MAX_EDGE_PX / Math.max(bmp.width, bmp.height));
  const w = Math.max(1, Math.round(bmp.width * scale));
  const h = Math.max(1, Math.round(bmp.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  bmp.draw(ctx, w, h);

  for (const q of QUALITY_LADDER) {
    const blob: Blob | null = await new Promise((res) => canvas.toBlob(res, "image/jpeg", q));
    if (blob && blob.size <= MAX_SUBJECT_PHOTO_BYTES) {
      return new File([blob], renameTo(file.name, "jpg"), { type: "image/jpeg" });
    }
  }
  return null;
}

export async function prepareSubjectPhoto(file: File): Promise<PreparedPhoto> {
  const kind = classifyUpload(file);

  if (kind === "document") {
    return { file: null, note: null, error: null };     // not a photo at all; caller routes it
  }

  if (kind === "photo") {
    return { file, note: null, error: null };           // already fine — do not touch it
  }

  if (kind === "photo-too-large") {
    const smaller = await shrink(file);
    if (smaller) {
      return { file: smaller, note: `${file.name} was resized to fit the 6 MB limit.`, error: null };
    }
    return { file: null, note: null, error: photoProblemHelp(kind, file.name) };
  }

  // kind === "photo-unsupported" — HEIC/HEIF is the case worth real effort.
  if (/\.(heic|heif)$/i.test(file.name) || /image\/hei[cf]/i.test(file.type || "")) {
    try {
      const mod = await import("heic2any");
      const heic2any = (mod as { default: (o: Record<string, unknown>) => Promise<Blob | Blob[]> }).default;
      const converted = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.9 });
      const blob = Array.isArray(converted) ? converted[0] : converted;
      if (blob) {
        let out = new File([blob], renameTo(file.name, "jpg"), { type: "image/jpeg" });
        // A HEIC is efficiently compressed, so its JPEG can be BIGGER — and can land over the
        // ceiling the conversion was meant to get us under.
        if (out.size > MAX_SUBJECT_PHOTO_BYTES) {
          const smaller = await shrink(out);
          if (!smaller) return { file: null, note: null, error: photoProblemHelp("photo-too-large", file.name) };
          out = smaller;
        }
        return { file: out, note: `${file.name} was converted from HEIC to JPEG.`, error: null };
      }
    } catch {
      /* fall through to the actionable message */
    }
  }

  return { file: null, note: null, error: photoProblemHelp(kind, file.name) };
}

/**
 * A prepared photo as the JPEG data URI the server accepts, scaled to the server's OWN bound
 * (2026-09-25). `amb_report._safe_subject_photo` re-encodes every agent photo to a 1100px long edge
 * anyway, so sending more is bytes the 8 MB JSON limit on `/api/amb/*` has to carry for nothing —
 * and the confirm body also carries every comparable. Null when the browser cannot draw the image
 * (the caller says so; it never sends the raw file instead).
 */
export async function photoToDataUri(file: File, maxEdge = 1100): Promise<string | null> {
  const bmp = await loadBitmap(file);
  if (!bmp || !bmp.width || !bmp.height) return null;
  const scale = Math.min(1, maxEdge / Math.max(bmp.width, bmp.height));
  const w = Math.max(1, Math.round(bmp.width * scale));
  const h = Math.max(1, Math.round(bmp.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  bmp.draw(ctx, w, h);
  try {
    const uri = canvas.toDataURL("image/jpeg", 0.85);
    return uri.startsWith("data:image/jpeg") ? uri : null;
  } catch {
    return null;
  }
}
